import React, { Component } from "react";
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import KodeDecodeText from "../effects/KodeDecodeText";
import { ACCENT_TEXT, FONT_DISPLAY } from "../../theme/tokens";
import { KodeGrid, KodeHaze } from "../effects/KodeEffects";
import {
  ACTIVE_WINDOW_DAYS,
  aggregateStatistics,
  hasFailures,
  hasRun,
  projectPassRate,
} from "../../utility/statistics";
import { formatRelativeTime, isStale } from "../../utility/relative-time";

const WELCOME_DECODE_KEY = "kode.home.decoded";

const styles = (theme) => ({
  root: {
    position: "relative",
    maxWidth: 1120,
    margin: "0 auto",
    paddingBottom: theme.spacing(8),
    // Дымка позиционируется относительно этого блока и свисает за него
    // (right: -16vmax), а выше по дереву ничего не обрезает переполнение —
    // страница ездила вбок на 173px. Здесь клип гарантирован, а не случаен;
    // на sign-in и 404 этот же приём уже работает.
    // Именно overflow, не overflow-x: на не-viewport элементе overflow-x
    // принудительно делает overflow-y: auto, а дымка свисает и вверх.
    overflow: "hidden",
  },
  hero: {
    position: "relative",
    zIndex: 1,
    paddingTop: theme.spacing(6),
  },
  eyebrow: {
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
    display: "block",
  },
  headline: {
    marginBottom: theme.spacing(1),
  },
  // Цвет — ACCENT_TEXT, а не ACCENT: строка мелкая, а основной акцент #4f4ff4
  // как текст допустим только от 20px/600 (контраст 3.76:1 на чёрном).
  // Здесь берём accent-2 с контрастом 5.68:1.
  tagline: {
    display: "block",
    color: ACCENT_TEXT,
    marginBottom: theme.spacing(0.5),
  },
  poweredBy: {
    display: "block",
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(3),
  },
  // Фирменный приём KODE: акцентный span внутри белого заголовка.
  headlineAccent: {
    color: theme.palette.primary.main,
    display: "block",
  },
  subline: {
    color: theme.palette.text.secondary,
    maxWidth: "60ch",
    fontSize: 16,
  },
  rule: {
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(5, 0),
  },
  // gap:1px на фоне волосяной линии даёт разделители без единого лишнего узла.
  //
  // Колонки заданы явно. Было repeat(auto-fit, minmax(180px, 1fr)) — четыре
  // плитки укладывались в три колонки на 640–780 и 960–1150, оставляя пустую
  // ячейку с видимой рамкой; на 1024 высота блока прыгала со 113px до 226px.
  // Два и четыре делят четыре плитки нацело всегда. Порог 1100, а не md:
  // выше 960 панель становится persistent и съедает 272px, поэтому четыре
  // колонки помещаются только с 1100.
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 1,
    backgroundColor: theme.palette.divider,
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.up(1100)]: {
      gridTemplateColumns: "repeat(4, 1fr)",
    },
  },
  statCell: {
    backgroundColor:
      theme.palette.type === "dark" ? "#0a0a0a" : theme.palette.background.paper,
    padding: theme.spacing(3),
  },
  statLabel: {
    color: theme.palette.text.disabled,
    display: "block",
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 800,
    fontSize: 40,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  // Не декоративная подпись, а часть смысла: слово ATTENTION выбрано мягким
  // сознательно и само по себе критерий не объясняет.
  statHint: {
    display: "block",
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.disabled,
  },
  sectionLabel: {
    color: theme.palette.text.disabled,
    display: "block",
    margin: theme.spacing(6, 0, 2),
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: theme.spacing(2),
  },
  card: {
    display: "block",
    padding: theme.spacing(2.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: "inherit",
    textDecoration: "none",
    transition: "border-color .2s ease, background-color .2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: "rgba(79,79,244,0.08)",
    },
    "&:hover $cardArrow": {
      transform: "translateX(4px)",
    },
  },
  cardGroup: {
    color: theme.palette.text.disabled,
    display: "block",
    marginBottom: theme.spacing(0.5),
    minHeight: 16,
  },
  cardName: {
    fontSize: 18,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardArrow: {
    color: theme.palette.primary.main,
    transition: "transform .2s ease",
  },
  cardStatus: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    fontSize: 12,
    color: theme.palette.text.secondary,
    // gap во flex роняет PostCSS в CRA 4, поэтому отступ — через margin
    "& > span": {
      marginRight: theme.spacing(1.5),
    },
  },
  // Заброшенный пайплайн видно, не открывая проект.
  cardStatusStale: {
    color: theme.palette.text.disabled,
  },
  cardPass: {
    color: theme.palette.success.main,
  },
  cardFail: {
    color: theme.palette.error.main,
  },
  empty: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(6),
    textAlign: "center",
  },
});

/**
 * Счётчики считаются по ответу GET /projects/statistics — allure уже сложил
 * статистику в reports/latest/widgets/summary.json, и сервер отдаёт её одним
 * запросом. Прежние Folders / Max depth / Largest group убраны: первый считал
 * промежуточные каталоги дерева, а не созданные папки (три созданные давали
 * цифру 10), второй описывал схему именования, заданную самим пользователем,
 * третий брал группу по первому сегменту project_id и игнорировал проекты в
 * корне.
 *
 * Общего pass rate здесь сознательно нет: KODE — аутсорс, проекты принадлежат
 * разным клиентам, и средний процент по несвязанным проектам не имеет
 * владельца. Процент живёт на карточке, где хозяин есть.
 */
class AllureDockerHomePage extends Component {
  render() {
    const { classes } = this.props;
    const projectIds = Object.keys(this.props.projects || {}).sort();
    const nowSeconds = Date.now() / 1000;
    // null от контейнера — «цифр нет»: запрос упал или ещё не доехал. Раньше
    // сюда приходил пустой массив, и отказ выглядел как правдоподобные нули:
    // «Projects 0 · Tests 0» и «no runs yet» на каждой карточке при живом
    // дереве слева. Молчать можно про отсутствие цифр, а не про их подмену.
    const statsUnavailable = this.props.statistics == null;
    const statistics = this.props.statistics || [];
    const statisticsById = {};
    statistics.forEach((item) => {
      statisticsById[item.project_id] = item;
    });
    const totals = aggregateStatistics(statistics, nowSeconds);
    const statValue = (value) => (statsUnavailable ? "—" : value);

    // Decode один раз за сессию: на каждой навигации домой это был бы тик.
    const alreadyDecoded = sessionStorage.getItem(WELCOME_DECODE_KEY) === "1";
    sessionStorage.setItem(WELCOME_DECODE_KEY, "1");

    return (
      <div className={classes.root}>
        <KodeHaze position="topRight" />
        <KodeGrid />

        <div className={classes.hero}>
          <Typography variant="overline" className={classes.eyebrow}>
            QA Platform
          </Typography>
          <Typography variant="h1" className={classes.headline} component="h1">
            Test reports for
            <span className={classes.headlineAccent}>
              {alreadyDecoded ? "KODE" : <KodeDecodeText text="KODE" />}
            </span>
          </Typography>
          {/* Два мелких ряда подряд не конкурируют за внимание, потому что
              различаются цветом: акцентный слоган и приглушённая приписка. */}
          <Typography variant="overline" className={classes.tagline}>
            A new age of quality
          </Typography>
          {/* Программа — приписка, а не адресат отчётов: «reports for the
              transformation» перевирало отношение. KODE не повторяем, он уже
              в заголовке и в логотипе. */}
          <Typography variant="overline" className={classes.poweredBy}>
            Powered by AI Transformation
          </Typography>
          <Typography className={classes.subline}>
            Allure reports for every service, in one place. Pick a project from the sidebar,
            or drag it into a folder to keep the tree tidy.
          </Typography>

          <div className={classes.rule} />

          <div className={classes.stats}>
            {[
              // Projects берём из дерева, а не из статистики: это число
              // известно всегда, даже когда цифры не доехали.
              ["Projects", projectIds.length, null],
              ["Tests", statValue(totals.tests.toLocaleString("en-US")), null],
              ["Active", statValue(totals.active), `last ${ACTIVE_WINDOW_DAYS} days`],
              ["Attention", statValue(totals.attention), "with failures"],
            ].map(([label, value, hint]) => (
              <div className={classes.statCell} key={label}>
                <Typography variant="overline" className={classes.statLabel}>
                  {label}
                </Typography>
                <div className={classes.statValue}>{value}</div>
                {hint ? (
                  <Typography variant="caption" className={classes.statHint}>
                    {hint}
                  </Typography>
                ) : null}
              </div>
            ))}
          </div>

          {/* Единственное место, где сказано, почему вместо цифр прочерки.
              Тост тут был бы тревогой не по делу: дерево и отчёты работают. */}
          {statsUnavailable ? (
            <Typography variant="caption" className={classes.statHint}>
              Statistics are unavailable right now
            </Typography>
          ) : null}

          <Typography variant="overline" className={classes.sectionLabel}>
            Projects A–Z
          </Typography>

          {projectIds.length === 0 ? (
            <div className={classes.empty}>
              <Typography variant="overline" className={classes.statLabel}>
                No projects
              </Typography>
              <Typography className={classes.subline} style={{ margin: "0 auto 24px" }}>
                Nothing has been reported yet. Create a project, then send results to it
                from your pipeline.
              </Typography>
              <Button variant="outlined" color="primary" disabled>
                Use the + button in the toolbar
              </Button>
            </div>
          ) : (
            <div className={classes.cards}>
              {projectIds.map((projectId) => {
                // Папка приходит полем `folder`, а не выводится из id: id плоский.
                const group = (this.props.projects[projectId] || {}).folder || "";
                const name = projectId;
                const item = statisticsById[projectId];
                const passRate = projectPassRate(item);
                const lastRun = item ? formatRelativeTime(item.generated_at, nowSeconds) : null;
                const stale = item ? isStale(item.generated_at, nowSeconds) : false;
                return (
                  <Link
                    key={projectId}
                    to={`/projects/${projectId}`}
                    className={classes.card}
                  >
                    <Typography variant="overline" className={classes.cardGroup}>
                      {group}
                    </Typography>
                    <div className={classes.cardName}>
                      {name}
                      <span className={classes.cardArrow}>→</span>
                    </div>
                    <div className={clsx(classes.cardStatus, stale && classes.cardStatusStale)}>
                      {hasRun(item) ? (
                        <React.Fragment>
                          {/* Пройденные и упавшие красятся раздельно: одним
                              спаном «3 ✓ · 2 ✗» целиком уходило в красный, и
                              счётчик пройденных читался как ошибка. У
                              заброшенного проекта статусные цвета не
                              применяются вовсе — иначе приглушение контейнера
                              не доставало бы до счётчиков и полумёртвый проект
                              кричал бы ярче живого. */}
                          <span className={stale ? undefined : classes.cardPass}>
                            {item.passed || 0} ✓
                          </span>
                          <span
                            className={
                              stale || !hasFailures(item) ? undefined : classes.cardFail
                            }
                          >
                            {(item.failed || 0) + (item.broken || 0)} ✗
                          </span>
                          {passRate === null ? null : <span>{Math.round(passRate)}%</span>}
                          <span>{lastRun}</span>
                        </React.Fragment>
                      ) : statsUnavailable ? (
                        // Про недоступные цифры карточка молчит: «no runs yet»
                        // здесь было бы прямой ложью о проекте с прогонами.
                        // Про причину говорит подпись под счётчиками.
                        null
                      ) : (
                        // Ноль читался бы как «всё упало», поэтому пустой проект
                        // говорит о себе словами.
                        <span>no runs yet</span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}
export default withStyles(styles, { withTheme: true })(AllureDockerHomePage);
